import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const NEW = "__new__";

/** Hızlı notu göreve dönüştürme modalı: proje + grup (yoksa oluştur),
 *  çoklu kişi atama (varsayılan: ben) ve görünürlük (varsayılan: Herkes). */
export default function ConvertTaskModal({ note, onClose, onConverted }) {
  const { user } = useAuthStore();
  const [title, setTitle] = useState(note?.text || "");
  const [projects, setProjects] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [newListName, setNewListName] = useState("To Do List");
  const [members, setMembers] = useState([]);
  const [assignees, setAssignees] = useState(user ? [user.id] : []);
  const [visibility, setVisibility] = useState("everyone");
  const [visibleTo, setVisibleTo] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const canCreate =
    !!user &&
    (["super_admin", "ultra_admin"].includes(user.role) ||
      user.can_create_projects);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data || []));
    api
      .get("/users", { params: { assignable: true } })
      .then(({ data }) => setMembers(data || []));
  }, []);

  useEffect(() => {
    if (!projectId || projectId === NEW) {
      setLists([]);
      setListId(projectId === NEW ? NEW : "");
      return;
    }
    api.get("/lists", { params: { project_id: projectId } }).then(({ data }) => {
      setLists(data || []);
      setListId(data?.[0]?.id || (canCreate ? NEW : ""));
    });
  }, [projectId, canCreate]);

  const filteredMembers = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    if (!s) return members;
    return members.filter((m) =>
      (m.name || m.email || "").toLocaleLowerCase("tr").includes(s),
    );
  }, [members, q]);

  const toggleIn = (arr, id) =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const canSubmit =
    title.trim() &&
    projectId &&
    (projectId !== NEW || newProjectName.trim()) &&
    (listId !== NEW ? !!listId : !!newListName.trim()) &&
    !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      let pid = projectId;
      let projName = projects?.find((p) => p.id === pid)?.name;
      if (pid === NEW) {
        const { data } = await api.post("/projects", {
          name: newProjectName.trim(),
        });
        pid = data.id;
        projName = data.name;
      }
      let lid = listId;
      if (lid === NEW) {
        const wanted = newListName.trim();
        // Aynı adda grup varsa yeniden oluşturma (yeni projede de geçerli).
        const { data: fresh } = await api.get("/lists", {
          params: { project_id: pid },
        });
        const existing = (fresh || []).find((l) => l.name === wanted);
        if (existing) lid = existing.id;
        else {
          const { data } = await api.post("/lists", {
            project_id: pid,
            name: wanted,
          });
          lid = data.id;
        }
      }
      await api.post("/tasks", {
        project_id: pid,
        list_id: lid,
        title: title.trim(),
        assignee_id: assignees[0] || null,
        assignee_ids: assignees,
        visibility,
        visible_to_ids: visibility === "custom" ? visibleTo : [],
      });
      await api.delete(`/quick-notes/${note.id}`);
      toast.success(`"${title.trim()}" göreve taşındı → ${projName || "Proje"} 🎉`);
      onConverted(note.id);
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        d === "cannot_create_projects"
          ? "Proje/grup oluşturma yetkiniz yok"
          : d || "Göreve taşınamadı",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="convert-task-modal">
        <DialogHeader>
          <DialogTitle>Göreve dönüştür</DialogTitle>
          <DialogDescription>
            Not göreve taşınır ve hızlı notlardan kaldırılır.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">Görev başlığı</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              data-testid="convert-title-input"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">Proje</label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="mt-1" data-testid="convert-project-select">
                <SelectValue
                  placeholder={projects === null ? "Yükleniyor…" : "Proje seç"}
                />
              </SelectTrigger>
              <SelectContent>
                {(projects || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
                {canCreate && (
                  <SelectItem value={NEW}>➕ Yeni proje oluştur</SelectItem>
                )}
              </SelectContent>
            </Select>
            {projectId === NEW && (
              <Input
                autoFocus
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Yeni proje adı"
                className="mt-2"
                data-testid="convert-new-project-input"
              />
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">Proje grubu</label>
            {projectId === NEW ? (
              <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="Yeni grup adı"
                className="mt-1"
                data-testid="convert-new-list-input"
              />
            ) : (
              <>
                <Select
                  value={listId}
                  onValueChange={setListId}
                  disabled={!projectId}
                >
                  <SelectTrigger className="mt-1" data-testid="convert-list-select">
                    <SelectValue placeholder={projectId ? "Grup seç" : "Önce proje seçin"} />
                  </SelectTrigger>
                  <SelectContent>
                    {lists.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.emoji ? `${l.emoji} ` : ""}
                        {l.name}
                      </SelectItem>
                    ))}
                    {canCreate && projectId && (
                      <SelectItem value={NEW}>➕ Yeni grup oluştur</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {listId === NEW && (
                  <Input
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="Yeni grup adı"
                    className="mt-2"
                    data-testid="convert-new-list-input"
                  />
                )}
              </>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">
              Kişiler{" "}
              <span className="font-normal text-zinc-400">
                ({assignees.length} seçili)
              </span>
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Kişi ara…"
                data-testid="convert-assignee-search"
                className="w-full rounded-md border border-zinc-200 py-1.5 pl-8 pr-3 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-200"
              />
            </div>
            <div className="mt-1 max-h-36 space-y-0.5 overflow-y-auto rounded-md border border-zinc-100 p-1">
              {filteredMembers.map((m) => (
                <label
                  key={m.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-zinc-50"
                >
                  <Checkbox
                    checked={assignees.includes(m.id)}
                    onCheckedChange={() =>
                      setAssignees((prev) => toggleIn(prev, m.id))
                    }
                    data-testid={`convert-assignee-${m.id}`}
                  />
                  <span className="truncate">
                    {m.name || m.email}
                    {m.id === user?.id && (
                      <span className="ml-1 text-xs text-zinc-400">(ben)</span>
                    )}
                  </span>
                </label>
              ))}
              {filteredMembers.length === 0 && (
                <p className="px-2 py-2 text-xs text-zinc-400">Kişi bulunamadı</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">Görünürlük</label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="mt-1" data-testid="convert-visibility-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">Herkes</SelectItem>
                <SelectItem value="private">Sadece Ben</SelectItem>
                <SelectItem value="participants">
                  Sadece Atanmışlar (oluşturan + sorumlular)
                </SelectItem>
                <SelectItem value="custom">Seçili kişiler</SelectItem>
              </SelectContent>
            </Select>
            {visibility === "custom" && (
              <div className="mt-1 max-h-28 space-y-0.5 overflow-y-auto rounded-md border border-zinc-100 p-1">
                {members.map((m) => (
                  <label
                    key={m.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-zinc-50"
                  >
                    <Checkbox
                      checked={visibleTo.includes(m.id)}
                      onCheckedChange={() =>
                        setVisibleTo((prev) => toggleIn(prev, m.id))
                      }
                      data-testid={`convert-visible-${m.id}`}
                    />
                    <span className="truncate">{m.name || m.email}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="convert-cancel-btn"
          >
            Vazgeç
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="bg-amber-500 text-white hover:bg-amber-600"
            data-testid="convert-submit-btn"
          >
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Göreve Taşı
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
